// /client/src/components/Analytics.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navigation from './Navigation';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const Analytics = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [deliveryTrend, setDeliveryTrend] = useState([]);
  const [engagementTrend, setEngagementTrend] = useState([]);
  const [podEngagement, setPodEngagement] = useState([]);
  const [activeDeployments, setActiveDeployments] = useState([]);
  const [recentReplies, setRecentReplies] = useState([]);
  const [trendDays, setTrendDays] = useState('7');

  // Fetch overview data
  const fetchOverview = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/analytics/overview?period=${trendDays}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch analytics');

      const data = await response.json();
      if (data.success) {
        setOverview(data);
      }
    } catch (err) {
      setError(err.message);
    }
  }, [trendDays]);

  // Fetch delivery trend
  const fetchDeliveryTrend = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/analytics/delivery-trend?days=${trendDays}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) return;

      const data = await response.json();
      if (data.success) {
        setDeliveryTrend(data.trend || []);
      }
    } catch (err) {
      console.warn('Could not fetch delivery trend:', err);
    }
  }, [trendDays]);

  // Fetch engagement trend
  const fetchEngagementTrend = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/analytics/engagement-trend?days=${trendDays}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) return;

      const data = await response.json();
      if (data.success) {
        setEngagementTrend(data.trend || []);
      }
    } catch (err) {
      console.warn('Could not fetch engagement trend:', err);
    }
  }, [trendDays]);

  // Fetch pod engagement
  const fetchPodEngagement = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/analytics/pod-engagement?limit=5&period=${trendDays}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) return;

      const data = await response.json();
      if (data.success) {
        setPodEngagement(data.pods || []);
      }
    } catch (err) {
      console.warn('Could not fetch pod engagement:', err);
    }
  }, [trendDays]);

  // Fetch active deployments
  const fetchActiveDeployments = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/analytics/active-deployments', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) return;

      const data = await response.json();
      if (data.success) {
        setActiveDeployments(data.deployments || []);
      }
    } catch (err) {
      console.warn('Could not fetch active deployments:', err);
    }
  }, []);

  // Fetch recent replies
  const fetchRecentReplies = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/analytics/recent-replies?limit=5&period=${trendDays}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) return;

      const data = await response.json();
      if (data.success) {
        setRecentReplies(data.replies || []);
      }
    } catch (err) {
      console.warn('Could not fetch recent replies:', err);
    }
  }, [trendDays]);

  // Helper to get period label
  const getPeriodLabel = () => {
    switch(trendDays) {
      case '7': return 'last 7 days';
      case '14': return 'last 14 days';
      case '30': return 'last 30 days';
      case '60': return 'last 60 days';
      case '90': return 'last 90 days';
      case 'ytd': return 'this year';
      case 'all': return 'all time';
      default: return 'this period';
    }
  };

  // Refetch all data when timeframe changes
  useEffect(() => {
    const refetchAll = async () => {
      setLoading(true);
      await fetchOverview();
      await Promise.all([
        fetchDeliveryTrend(),
        fetchEngagementTrend(),
        fetchPodEngagement(),
        fetchActiveDeployments(),
        fetchRecentReplies()
      ]);
      setLoading(false);
    };
    refetchAll();
  }, [trendDays, fetchOverview, fetchDeliveryTrend, fetchEngagementTrend, fetchPodEngagement, fetchActiveDeployments, fetchRecentReplies]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Format trend data for charts
  const formattedDeliveryTrend = deliveryTrend.map(d => ({
    ...d,
    date: formatDate(d.date)
  }));

  const formattedEngagementTrend = engagementTrend.map(d => ({
    ...d,
    date: formatDate(d.date)
  }));

  if (loading) {
    return (
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className='is-flex is-flex-direction-column'>
          <Navigation />
          <div className='mt-2 pt-6 container px-6'>
            <div className="has-text-centered py-6">
              <p>Loading analytics...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className='is-flex is-flex-direction-column'>
          <Navigation />
          <div className='mt-2 pt-6 container px-6'>
            <div className="notification is-danger">
              {error}
            </div>
          </div>
        </div>
      </section>
    );
  }

  const { summary, delivery, engagement, recentActivity } = overview || {};

  return (
    <section className="theme-light m-0 p-0" data-theme="light">
      <div className='is-flex is-flex-direction-column'>
        <Navigation />

        <div className='mt-2 pt-6 pb-6 container px-6'>
          {/* Header */}
          <div className="is-flex is-justify-content-space-between is-align-items-center mb-5">
            <div>
              <h1 className='h1 my-2'>Analytics</h1>
              <p className="has-text-grey">
                Track engagement and measure impact across your programs.
              </p>
            </div>
            <div className="select">
              <select 
                value={trendDays} 
                onChange={(e) => setTrendDays(e.target.value)}
              >
                <option value="7">Last 7 days</option>
                <option value="14">Last 14 days</option>
                <option value="30">Last 30 days</option>
                <option value="60">Last 60 days</option>
                <option value="90">Last 90 days</option>
                <option value="ytd">This year</option>
                <option value="all">All time</option>
              </select>
            </div>
          </div>

          {/* Summary Cards Row */}
          <div className="columns is-multiline mb-4">
            {/* Contacts */}
            <div className="column is-one-quarter">
              <div className="box" style={{ borderLeft: '4px solid #3273dc', height: '140px' }}>
                <p className="heading has-text-grey">Total Contacts</p>
                <p className="title is-3 mb-1">{summary?.contacts?.total?.toLocaleString() || 0}</p>
                <p className="is-size-7 has-text-grey">
                  {summary?.contacts?.participants || 0} participants • {summary?.contacts?.smsOptedIn || 0} SMS opted-in
                </p>
                <span className={`tag mt-2 ${recentActivity?.newContacts > 0 ? 'is-success' : 'is-light'}`}>
                  {recentActivity?.newContacts > 0 ? '+' : ''}{recentActivity?.newContacts || 0} {getPeriodLabel()}
                </span>
              </div>
            </div>

            {/* Active Pods */}
            <div className="column is-one-quarter">
              <div className="box" style={{ borderLeft: '4px solid #00d1b2', height: '140px' }}>
                <p className="heading has-text-grey">Active Pods</p>
                <p className="title is-3 mb-1">{summary?.pods?.active?.toLocaleString() || 0}</p>
                <p className="is-size-7 has-text-grey">
                  {summary?.pods?.total || 0} total • {summary?.pods?.inDelivery || 0} in delivery
                </p>
                <span className={`tag mt-2 ${recentActivity?.newPods > 0 ? 'is-success' : 'is-light'}`}>
                  {recentActivity?.newPods > 0 ? '+' : ''}{recentActivity?.newPods || 0} {getPeriodLabel()}
                </span>
              </div>
            </div>

            {/* Deployments */}
            <div className="column is-one-quarter">
              <div className="box" style={{ borderLeft: '4px solid #ffdd57', height: '140px' }}>
                <p className="heading has-text-grey">Deployments</p>
                <p className="title is-3 mb-1">{summary?.deployments?.total?.toLocaleString() || 0}</p>
                <p className="is-size-7 has-text-grey">
                  {summary?.deployments?.inDelivery || 0} active • {summary?.deployments?.completed || 0} completed
                </p>
                <span className={`tag mt-2 ${recentActivity?.newDeployments > 0 ? 'is-success' : 'is-light'}`}>
                  {recentActivity?.newDeployments > 0 ? '+' : ''}{recentActivity?.newDeployments || 0} {getPeriodLabel()}
                </span>
              </div>
            </div>

            {/* Products */}
            <div className="column is-one-quarter">
              <div className="box" style={{ borderLeft: '4px solid #ff3860', height: '140px' }}>
                <p className="heading has-text-grey">Products</p>
                <p className="title is-3 mb-1">{summary?.products?.active?.toLocaleString() || 0}</p>
                <p className="is-size-7 has-text-grey">
                  {summary?.products?.total || 0} total • {summary?.programs?.active || 0} active programs
                </p>
                <span className={`tag mt-2 ${recentActivity?.newProducts > 0 ? 'is-success' : 'is-light'}`}>
                  {recentActivity?.newProducts > 0 ? '+' : ''}{recentActivity?.newProducts || 0} {getPeriodLabel()}
                </span>
              </div>
            </div>
          </div>

          {/* Delivery Metrics Row */}
          <div className="columns mb-4">
            <div className="column">
              <div className="box">
                <div className="is-flex is-justify-content-space-between is-align-items-center mb-3">
                  <h2 className="subtitle is-5 mb-0 has-text-weight-bold">Delivery Metrics</h2>
                  <span className="tag is-info is-light" style={{ textTransform: 'capitalize' }}>{getPeriodLabel()}</span>
                </div>
                <div className="columns is-mobile">
                  <div className="column has-text-centered">
                    <p className="heading has-text-grey">Messages Sent</p>
                    <p className="title is-4 has-text-success">{delivery?.sent?.toLocaleString() || 0}</p>
                  </div>
                  <div className="column has-text-centered">
                    <p className="heading has-text-grey">Delivery Rate</p>
                    <p className="title is-4">{delivery?.deliveryRate || 0}%</p>
                  </div>
                  <div className="column has-text-centered">
                    <p className="heading has-text-grey">Failed</p>
                    <p className="title is-4 has-text-danger">{delivery?.failed?.toLocaleString() || 0}</p>
                  </div>
                  <div className="column has-text-centered">
                    <p className="heading has-text-grey">Pending</p>
                    <p className="title is-4 has-text-warning">{(delivery?.pending + delivery?.queuedToTwilio)?.toLocaleString() || 0}</p>
                  </div>
                  <div className="column has-text-centered">
                    <p className="heading has-text-grey">Total Replies</p>
                    <p className="title is-4 has-text-info">{engagement?.totalReplies?.toLocaleString() || 0}</p>
                  </div>
                  <div className="column has-text-centered">
                    <p className="heading has-text-grey">Response Rate</p>
                    <p className="title is-4">{engagement?.responseRate || 0}%</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Charts Row */}
          <div className="columns mb-4">
            {/* Delivery Trend Chart */}
            <div className="column is-half">
              <div className="box" style={{ height: '320px' }}>
                <h2 className="subtitle is-5 mb-3 has-text-weight-bold">Message Delivery Trend</h2>
                {formattedDeliveryTrend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={formattedDeliveryTrend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="sent" fill="#48c78e" name="Sent" />
                      <Bar dataKey="failed" fill="#f14668" name="Failed" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="has-text-centered has-text-grey py-6">No delivery data available</p>
                )}
              </div>
            </div>

            {/* Engagement Trend Chart */}
            <div className="column is-half">
              <div className="box" style={{ height: '320px' }}>
                <h2 className="subtitle is-5 mb-3 has-text-weight-bold">Reply Engagement Trend</h2>
                {formattedEngagementTrend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={formattedEngagementTrend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="replies" stroke="#3273dc" strokeWidth={2} name="Replies" />
                      <Line type="monotone" dataKey="withMedia" stroke="#00d1b2" strokeWidth={2} name="With Media" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="has-text-centered has-text-grey py-6">No engagement data available</p>
                )}
              </div>
            </div>
          </div>

          {/* Bottom Row - Pod Engagement, Active Deployments, Recent Replies */}
          <div className="columns">
            {/* Pod Engagement */}
            <div className="column is-one-third">
              <div className="box" style={{ height: '320px', display: 'flex', flexDirection: 'column' }}>
                <h2 className="subtitle is-5 mb-3 has-text-weight-bold">Top Pod Engagement</h2>
                {podEngagement.length > 0 ? (
                  <div style={{ overflowY: 'auto', flex: 1 }}>
                    {podEngagement.map((pod, idx) => (
                      <div 
                        key={pod.podId || idx} 
                        className="mb-3 p-3"
                        style={{ backgroundColor: '#f5f5f5', borderRadius: '6px' }}
                      >
                        <div className="is-flex is-justify-content-space-between is-align-items-center">
                          <strong className="is-size-7 colorBlack">{pod.podName}</strong>
                          <span className="tag is-primary is-light">{pod.responseRate}%</span>
                        </div>
                        <p className="is-size-7 has-text-grey mt-1">
                          {pod.replies} replies / {pod.sent} sent
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="has-text-centered has-text-grey is-size-7">No pod engagement data</p>
                )}
              </div>
            </div>

            {/* Active Deployments */}
            <div className="column is-one-third">
              <div className="box" style={{ height: '320px', display: 'flex', flexDirection: 'column' }}>
                <h2 className="subtitle is-5 mb-3 has-text-weight-bold">Active Deployments</h2>
                {activeDeployments.length > 0 ? (
                  <div style={{ overflowY: 'auto', flex: 1 }}>
                    {activeDeployments.map((dep) => (
                      <div 
                        key={dep._id} 
                        className="mb-3 p-3 colorBlack"
                        style={{ backgroundColor: '#f5f5f5', borderRadius: '6px', cursor: 'pointer' }}
                        onClick={() => navigate(`/deployments/${dep._id}`)}
                      >
                        <div className="is-flex is-justify-content-space-between is-align-items-center">
                          <strong className="is-size-7 colorBlue">{dep.name}</strong>
                          <span className={`tag is-small ${dep.status === 'in delivery' ? 'is-warning' : 'is-info'}`}>
                            {dep.status === 'in delivery' ? 'In Delivery' : 'Scheduled'}
                          </span>
                        </div>
                        <p className="is-size-7 has-text-grey mt-1">
                          {dep.stats?.sent || 0} sent • {dep.stats?.pending || 0} pending
                        </p>
                        <div className="mt-2" style={{ height: '4px', backgroundColor: '#e0e0e0', borderRadius: '2px' }}>
                          <div 
                            style={{ 
                              height: '100%', 
                              width: `${dep.progress || 0}%`, 
                              backgroundColor: '#48c78e',
                              borderRadius: '2px'
                            }} 
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="has-text-centered has-text-grey is-size-7">No active deployments</p>
                )}
              </div>
            </div>

            {/* Recent Replies */}
            <div className="column is-one-third">
              <div className="box" style={{ height: '320px', display: 'flex', flexDirection: 'column' }}>
                <h2 className="subtitle is-5 mb-3 has-text-weight-bold">Recent Replies</h2>
                {recentReplies.length > 0 ? (
                  <div style={{ overflowY: 'auto', flex: 1 }}>
                    {recentReplies.map((reply, idx) => (
                      <div 
                        key={reply._id || idx} 
                        className="mb-3 p-2"
                        style={{ backgroundColor: '#f5f5f5', borderRadius: '6px', cursor: 'pointer' }}
                        onClick={() => reply.podId && navigate(`/pods/${reply.podId}`)}
                      >
                        <div className="is-flex is-justify-content-space-between is-size-7 mb-1">
                          <span className='colorBlack'>{reply.senderName}</span>
                          <span className="has-text-grey">{formatDateTime(reply.receivedAt)}</span>
                        </div>
                        <p className="is-size-7" style={{ 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap' 
                        }}>
                          {reply.hasMedia && ''}
                          {reply.body || '(media only)'}
                        </p>
                        <p className="is-size-7 has-text-grey mt-1">
                          Pod: {reply.podName}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="has-text-centered has-text-grey is-size-7">No replies yet</p>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Analytics;